/**
 * OrgSwitcher — Client context switcher for multi-org users.
 *
 * Renders a compact dropdown in the sidebar that lets users switch
 * their active client context. On switch:
 * 1. Updates localStorage (active_organization_id)
 * 2. Clears active_workspace_id so backend resolves the target org default
 * 3. Re-bootstraps the session (which updates ThemeProvider automatically)
 * 4. Axios interceptor picks up the new X-Organization-Id header
 *
 * Only renders when there is at least one client option, or when a
 * platform admin can use the cross-client scope.
 */

import { useState, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useSession } from "@/features/auth/useSession";
import { useTheme } from "@/features/shell/ThemeProvider";
import { Building2, ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import type { BootstrapOrganization } from "@/lib/bootstrap-types";
import { RailTooltip } from "@/components/layout/RailTooltip";
import { useI18n } from "@/lib/i18n";

interface OrgSwitcherProps {
  /** When true, only show the org icon (for collapsed sidebar) */
  collapsed?: boolean;
  /** Told when the client menu opens or closes (the sidebar holds its peek). */
  onOpenChange?: (open: boolean) => void;
}

const CLIENT_ORG_TYPES = new Set(["customer"]);

export function OrgSwitcher({
  collapsed = false,
  onOpenChange,
}: OrgSwitcherProps) {
  const { session, refreshSession } = useSession();
  const { isCustomDomain } = useTheme();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const organizations = session?.organizations ?? [];
  const clientOrganizations = organizations.filter((org) =>
    CLIENT_ORG_TYPES.has(org.org_type),
  );
  const activeOrgId = session?.active_organization?.id;
  const activeClient = clientOrganizations.find((o) => o.id === activeOrgId);
  const activeOrgIsNonClient = Boolean(
    activeOrgId &&
    !activeClient &&
    organizations.some((org) => org.id === activeOrgId),
  );

  // Detect Sentinels platform admins for cross-client "All clients" scope.
  const isPlatformAdmin =
    session?.permissions?.security_role === "platform_admin";
  const canUseAllClients =
    isPlatformAdmin &&
    organizations.some((org) => org.org_type === "platform_owner");
  const storedActiveOrgId = localStorage.getItem("active_organization_id");

  // Show "All" sentinel when cross-tenant is active, or when bootstrap still
  // reports a technical/operator org that is not a valid client selector item.
  const isAllClientsActive =
    canUseAllClients && (!storedActiveOrgId || activeOrgIsNonClient);

  // NOTE (#388): hooks MUST be declared before any early-return. The previous
  // version had `useCallback` calls below `if (isCustomDomain) return null;`
  // and `if (organizations.length < 2 && !isPlatformAdmin) return null;`,
  // which violated react-hooks/rules-of-hooks (when the early-return path was
  // taken on render N and not on render N+1, the hook order changed between
  // renders). Both callbacks are now declared up-front; the early returns
  // happen further down. The callbacks are safe to declare unconditionally
  // because they only read closure values (no side effects at declaration time).
  const handleSwitch = useCallback(
    async (org: BootstrapOrganization) => {
      if (org.id === activeOrgId) {
        setOpen(false);
        return;
      }

      setSwitching(true);
      await queryClient.cancelQueries();

      // 1. Update active client organization in localStorage
      localStorage.setItem("active_organization_id", org.id);

      // 2. Drop stale workspace from the previous client. Bootstrap will
      // resolve the default active workspace for the selected organization.
      localStorage.removeItem("active_workspace_id");

      // 3. Re-bootstrap session — ThemeProvider will pick up the new branding
      try {
        await refreshSession();
        await queryClient.invalidateQueries({ refetchType: "active" });
      } finally {
        setSwitching(false);
        setOpen(false);
      }
    },
    [activeOrgId, queryClient, refreshSession],
  );

  const handleSwitchAll = useCallback(async () => {
    setSwitching(true);
    await queryClient.cancelQueries();

    // Remove org/workspace from localStorage so the API interceptor
    // won't send X-Organization-Id — backend sees cross_tenant mode.
    localStorage.removeItem("active_organization_id");
    localStorage.removeItem("active_workspace_id");

    try {
      await refreshSession();
      await queryClient.invalidateQueries({ refetchType: "active" });
    } finally {
      setSwitching(false);
      setOpen(false);
    }
  }, [queryClient, refreshSession]);

  useEffect(() => {
    if (!canUseAllClients || !activeOrgId || !activeOrgIsNonClient) return;
    if (localStorage.getItem("active_organization_id") !== activeOrgId) return;

    localStorage.removeItem("active_organization_id");
    localStorage.removeItem("active_workspace_id");
  }, [activeOrgId, activeOrgIsNonClient, canUseAllClients]);

  // Early returns must come AFTER all hook calls to keep hook ordering stable
  // across renders. See the #388 note above.
  if (isCustomDomain) return null;
  if (clientOrganizations.length < 1 && !canUseAllClients) return null;

  if (collapsed) {
    const collapsedLabel = isAllClientsActive
      ? t("common.allClients")
      : (activeClient?.name ?? t("common.selectClient"));
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <RailTooltip enabled menuOpen={open} content={collapsedLabel}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={collapsedLabel}
              className="flex w-full items-center justify-center rounded-md py-2 text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-none"
            >
              <Building2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
        </RailTooltip>
        <DropdownMenuContent side="right" align="start" className="w-56">
          <DropdownMenuLabel className="text-muted-foreground text-xs">
            {t("common.client")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {canUseAllClients && (
            <DropdownMenuItem
              onClick={() => void handleSwitchAll()}
              disabled={switching}
              className="cursor-pointer"
            >
              <div className="flex w-full items-center gap-2">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 to-purple-600 text-[10px] font-bold text-white">
                  ✦
                </div>
                <span className="flex-1 truncate text-sm font-medium">
                  {t("common.allClients")}
                </span>
                {isAllClientsActive && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-green-500" />
                )}
              </div>
            </DropdownMenuItem>
          )}
          {clientOrganizations.map((org) => (
            <DropdownMenuItem
              key={org.id}
              onClick={() => void handleSwitch(org)}
              disabled={switching}
              className="cursor-pointer"
            >
              <div className="flex w-full items-center gap-2">
                <OrgAvatar org={org} size="sm" />
                <span className="flex-1 truncate text-sm">{org.name}</span>
                {org.id === activeOrgId && !isAllClientsActive && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-green-500" />
                )}
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-2.5 py-2",
            "text-white/80 hover:bg-white/[0.06] hover:text-white",
            "text-left transition-colors",
            switching && "pointer-events-none opacity-50",
          )}
        >
          {isAllClientsActive ? (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 to-purple-600 text-[10px] font-bold text-white">
              ✦
            </div>
          ) : (
            <OrgAvatar org={activeClient} size="sm" />
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">
              {isAllClientsActive
                ? t("common.allClients")
                : (activeClient?.name ?? t("common.selectClient"))}
            </p>
            <p className="truncate font-mono text-[10px] text-white/50">
              {isAllClientsActive
                ? t("common.clientScope")
                : activeClient?.slug}
            </p>
          </div>
          <ChevronDown className="h-3 w-3 shrink-0 text-white/40" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-muted-foreground text-xs">
          {t("common.client")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canUseAllClients && (
          <DropdownMenuItem
            onClick={() => void handleSwitchAll()}
            disabled={switching}
            className="cursor-pointer"
          >
            <div className="flex w-full items-center gap-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-indigo-500 to-purple-600 text-xs font-bold text-white">
                ✦
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {t("common.allClients")}
                </p>
                <p className="text-muted-foreground truncate font-mono text-xs">
                  {t("common.clientScope")}
                </p>
              </div>
              {isAllClientsActive && (
                <Check className="h-4 w-4 shrink-0 text-green-500" />
              )}
            </div>
          </DropdownMenuItem>
        )}
        {clientOrganizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => void handleSwitch(org)}
            disabled={switching}
            className="cursor-pointer"
          >
            <div className="flex w-full items-center gap-2.5">
              <OrgAvatar org={org} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{org.name}</p>
                <p className="text-muted-foreground truncate font-mono text-xs">
                  {org.slug}
                </p>
              </div>
              {org.id === activeOrgId && !isAllClientsActive && (
                <Check className="h-4 w-4 shrink-0 text-green-500" />
              )}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Org avatar — shows first letter with brand color or neutral fallback
// ---------------------------------------------------------------------------

function OrgAvatar({
  org,
  size = "sm",
}: {
  org?: BootstrapOrganization;
  size?: "sm" | "md";
}) {
  const brandColor =
    (org?.branding_config?.theme as Record<string, string> | undefined)
      ?.sidebar_bg ?? "#6366f1";
  const letter = org?.name?.charAt(0)?.toUpperCase() ?? "?";
  const dim = size === "sm" ? "h-6 w-6 text-[10px]" : "h-7 w-7 text-xs";

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md font-bold text-white",
        dim,
      )}
      style={{ backgroundColor: brandColor }}
    >
      {letter}
    </div>
  );
}
